import { ViewportPortal, useStore } from '@xyflow/react';
import type { CanvasAwarenessState } from './awareness-types';
import { pathFor } from '../edges/edge-visuals';
import overlayStyles from './awareness-overlay.module.css';

export interface RemoteEdgeHighlightsProps {
  remoteStates: Map<number, CanvasAwarenessState>;
}

/**
 * Renders colored overlays on edges selected by remote peers. Uses RF's
 * store to read edge geometry (sourceX/Y, targetX/Y, positions, curvature)
 * and reuses pathFor() to draw the same path shape as NulaEdge — so the
 * highlight tracks the actual rendered path exactly.
 */
export function RemoteEdgeHighlights({ remoteStates }: RemoteEdgeHighlightsProps) {
  // Collect edge ids that any remote peer has selected, mapped to their color.
  const edgeColors = new Map<string, string>();
  remoteStates.forEach((state) => {
    const ids = Array.isArray(state.canvasEdgeSelection) ? state.canvasEdgeSelection : [];
    ids.forEach((id) => {
      if (!edgeColors.has(id)) edgeColors.set(id, state.user.color);
    });
  });

  if (edgeColors.size === 0) return null;

  return (
    <ViewportPortal>
      <RemoteEdgeHighlightsSVG edgeColors={edgeColors} />
    </ViewportPortal>
  );
}

function RemoteEdgeHighlightsSVG({ edgeColors }: { edgeColors: Map<string, string> }) {
  const edgeLookup = useStore((s) => s.edgeLookup);
  const nodeLookup = useStore((s) => s.nodeLookup);

  const paths: Array<{ id: string; d: string; color: string; strokeWidth: number }> = [];

  edgeColors.forEach((color, edgeId) => {
    const edge = edgeLookup.get(edgeId);
    if (!edge) return;

    const sourceNode = nodeLookup.get(edge.source);
    const targetNode = nodeLookup.get(edge.target);
    if (!sourceNode || !targetNode) return;

    // RF computes sourceX/Y etc. inside EdgeWrapper — we need to replicate
    // that. The simplest approximation: use node center positions. For edges
    // with handles, RF's getEdgePosition would give exact coords, but center
    // is close enough for a highlight overlay.
    const sw = sourceNode.measured?.width ?? sourceNode.width ?? 0;
    const sh = sourceNode.measured?.height ?? sourceNode.height ?? 0;
    const tw = targetNode.measured?.width ?? targetNode.width ?? 0;
    const th = targetNode.measured?.height ?? targetNode.height ?? 0;
    const sp = sourceNode.internals.positionAbsolute;
    const tp = targetNode.internals.positionAbsolute;

    const sourceX = sp.x + sw / 2;
    const sourceY = sp.y + sh / 2;
    const targetX = tp.x + tw / 2;
    const targetY = tp.y + th / 2;

    const curvature = (edge.data?.curvature as string | undefined) ?? 'smooth';
    const strokeWidth = (edge.data?.strokeWidth as number | undefined) ?? 2;

    const [d] = pathFor(curvature as 'straight' | 'smooth' | 'step', {
      sourceX,
      sourceY,
      sourcePosition: sourceNode.internals.handleBounds?.source?.[0]?.position ?? 'right' as never,
      targetX,
      targetY,
      targetPosition: targetNode.internals.handleBounds?.target?.[0]?.position ?? 'left' as never,
    });

    paths.push({ id: edgeId, d, color, strokeWidth });
  });

  if (paths.length === 0) return null;

  return (
    <svg className={overlayStyles.overlay} width={0} height={0}>
      {paths.map(({ id, d, color, strokeWidth }) => (
        <path
          key={id}
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth + 4}
          strokeOpacity={0.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
